import type { ComponentPropsWithRef } from "react";

import { ErrorBoundary } from "react-error-boundary";

import Sentry from "@similarweb/sentry-logger";

import { SI_SENTRY_TAGS } from "app/pages/sales-intelligence/constants/sentry";

type ErrorInfo = {
    componentStack: string;
};

type Props = ComponentPropsWithRef<typeof ErrorBoundary>;

const ErrorBoundaryWithLogging = (props: Props) => {
    // React rethrows whatever was thrown, not necessarily an Error — reading `.message` off a
    // string yielded `undefined`, which Sentry rendered as "No error message".
    const handleLoggedError = (error: unknown, info: ErrorInfo): void => {
        const normalizedError = error instanceof Error ? error : new Error(String(error), { cause: error });

        Sentry.error(normalizedError.message, normalizedError, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            tags: { solution: SI_SENTRY_TAGS.solution, ErrorType: "ErrorBoundary" }, // ErrorType tag is used for creating new alerts in sentry
        });

        props.onError?.(normalizedError, info);
    };

    return (
        <ErrorBoundary
            {...props}
            onError={handleLoggedError}
        />
    );
};

export default ErrorBoundaryWithLogging;
